import CustomLayoutGeneralSettings from '@/components/CustomLayoutGeneralSettings';
import InformationBanner from '@/components/InformationBanner';
import RegularText from '@/components/RegularText';
import TimePicker from '@/components/TimePicker';
import { initWeekWorkingDays } from '@/constants';
import { CutOffTimeTypes } from '@/constants/enum';
import CutOffTimeConfig from '@/pages/GeneralSettings/components/DateAndTime/CutOffTimeConfig';
import bannerSlice, { bannerSelector } from '@/redux/slice/banner.slice';
import settingsSlice, { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IParamsApi } from '@/types/apis/params';
import { Button, Checkbox, RadioButton, Tooltip } from '@shopify/polaris';
import { DuplicateIcon } from '@shopify/polaris-icons';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const CutOffTime = () => {
  const dispatch = useDispatch();
  const { isVisibleDateAndTime: isVisible } = useSelector(bannerSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const weekWorkingDays = dataSettings?.week_working_days || initWeekWorkingDays;
  const isUseSeparateWorkingDays = dataSettings?.isUseSeparateWorkingDays;

  const getWeekWorkingDays = useCallback(
    (type: CutOffTimeTypes) => {
      if (dataSettings) {
        const enable: boolean[] = [];
        const cut_off_after: string[] = [];
        const data: IParamsApi.IWeekWorkingDay[] = weekWorkingDays[type] || [];
        data.forEach((item) => {
          enable.push(item.enable === 1);
          cut_off_after.push(item.cut_off_after || '23:59');
        });
        return {
          enable,
          cut_off_after,
        };
      }
    },
    [dataSettings, weekWorkingDays],
  );

  const handleWeekWorkingDays = useCallback(
    (type: CutOffTimeTypes, workingDays: { enable: boolean[]; cut_off_after: string[] }) => {
      if (dataSettings) {
        const result = workingDays.enable.map((item, index) => {
          return {
            enable: item ? 1 : 0,
            cut_off_after: workingDays.cut_off_after[index],
            day: index,
          };
        });

        const newWeekWorkingDays = { ...weekWorkingDays, [type]: result };

        dispatch(
          settingsSlice.actions.handleDataSettings({
            ...dataSettings,
            week_working_days: newWeekWorkingDays,
          }),
        );
      }
    },
    [dataSettings, dispatch, weekWorkingDays],
  );

  const renderEnable = useCallback(
    (type: CutOffTimeTypes) => {
      const currentWorkingDays = getWeekWorkingDays(type);
      const result = currentWorkingDays?.enable.map((checked, index) => {
        return (
          <Checkbox
            key={index}
            label=""
            checked={checked}
            onChange={(value) => {
              const newEnable = [...currentWorkingDays.enable];
              newEnable[index] = value;
              handleWeekWorkingDays(type, {
                ...currentWorkingDays,
                enable: newEnable,
              });
            }}
          />
        );
      });
      return result || [];
    },
    [getWeekWorkingDays, handleWeekWorkingDays],
  );

  const renderCutOffDays = useCallback(
    (type: CutOffTimeTypes) => {
      const currentWorkingDays = getWeekWorkingDays(type);
      const result = currentWorkingDays?.cut_off_after.map((time, index) => {
        const timeArr = time?.split(':');
        const hour = timeArr[0];
        const minute = timeArr[1];
        const setHour = (value: string) => {
          const newCutOffDays = [...currentWorkingDays.cut_off_after];
          newCutOffDays[index] = [value, minute].join(':');
          handleWeekWorkingDays(type, {
            ...currentWorkingDays,
            cut_off_after: newCutOffDays,
          });
        };
        const setMinute = (value: string) => {
          const newCutOffDays = [...currentWorkingDays.cut_off_after];
          newCutOffDays[index] = [hour, value].join(':');
          handleWeekWorkingDays(type, {
            ...currentWorkingDays,
            cut_off_after: newCutOffDays,
          });
        };
        return (
          <div key={time}>
            <TimePicker
              disabled={!currentWorkingDays?.enable[index]}
              hour={hour}
              minute={minute}
              setHour={setHour}
              setMinute={setMinute}
            />
          </div>
        );
      });
      return result || [];
    },
    [getWeekWorkingDays, handleWeekWorkingDays],
  );

  const handleCopyTime = useCallback(
    (type: CutOffTimeTypes) => {
      const currentWorkingDays = weekWorkingDays[type];
      if (currentWorkingDays) {
        const newWorkingDays = currentWorkingDays.map((item) => {
          return {
            ...item,
            cut_off_after: currentWorkingDays[0].cut_off_after,
          };
        });
        const newWeekWorkingDays = { ...weekWorkingDays, [type]: newWorkingDays };
        dispatch(
          settingsSlice.actions.handleDataSettings({
            ...dataSettings,
            week_working_days: newWeekWorkingDays,
          }),
        );
      }
    },
    [dataSettings, dispatch, weekWorkingDays],
  );

  const renderRows = useCallback(
    (type: CutOffTimeTypes) => {
      const dayInWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const result = dayInWeek.map((item, index) => {
        return [
          <div key={item} className="d-flex align-center">
            <span>{renderEnable(type)[index] || null}</span>
            <RegularText>{item}</RegularText>
          </div>,
          renderCutOffDays(type)[index] || null,
          index === 0 ? (
            <div className="d-flex align-center content-center">
              <Tooltip content="Copy time to all">
                <Button icon={DuplicateIcon} variant="monochromePlain" onClick={() => handleCopyTime(type)} />
              </Tooltip>
            </div>
          ) : null,
        ];
      });
      return result;
    },
    [handleCopyTime, renderCutOffDays, renderEnable],
  );

  return (
    <CustomLayoutGeneralSettings
      title="Workdays and cut-off time"
      description={<p>Excluding non-workdays from preparation and delivery time</p>}
      isShowDivider
    >
      <div className={isVisible ? 'mt-12' : ''}>
        <InformationBanner
          isVisible={isVisible}
          onDismiss={() => dispatch(bannerSlice.actions.handleVisibleBanner({ key: 'isVisibleDateAndTime', value: false }))}
        >
          Orders after cut-off time will be counted as orders for the next day. Disabled days will not be counted in the estimated
          date. Time must be in 24-hour format (hh:mm).
        </InformationBanner>
      </div>

      <div className={`mt-8 d-flex flex-column`}>
        <RadioButton
          label="Apply same time to preparation and delivery"
          checked={!isUseSeparateWorkingDays}
          onChange={() =>
            dispatch(settingsSlice.actions.handleDataSettings({ ...dataSettings, isUseSeparateWorkingDays: false }))
          }
        />

        <RadioButton
          label="Set separate times for preparation and delivery"
          checked={!!isUseSeparateWorkingDays}
          onChange={() => dispatch(settingsSlice.actions.handleDataSettings({ ...dataSettings, isUseSeparateWorkingDays: true }))}
        />
      </div>
      {!isUseSeparateWorkingDays ? (
        <CutOffTimeConfig renderRows={renderRows(CutOffTimeTypes.PrepareAndDelivery)} className="mt-12" />
      ) : (
        <div className="mt-12 d-flex flex-column gap-12">
          <CutOffTimeConfig
            title="Preparation time"
            renderRows={renderRows(CutOffTimeTypes.Prepare)}
            className="d-flex flex-column gap-8"
          />
          <CutOffTimeConfig
            title="Delivery time"
            renderRows={renderRows(CutOffTimeTypes.Delivery)}
            className="mt-4 d-flex flex-column gap-8"
          />
        </div>
      )}
    </CustomLayoutGeneralSettings>
  );
};

export default CutOffTime;
